import React, { useState } from 'react';
import { set } from 'lodash';
import PropTypes from 'prop-types';
import { Col, FormGroup, FormRadioGroup, InPageAlert, Input, Row } from '@snsw/react-component-library';
import styled from 'styled-components';
import {
    ActionType,
    BusinessDisasterEligibility,
    ImpactedAddressMode,
    useApplicationConfig,
    useApplicationContext
} from '../../store';
import { getHelpMessage, getLabel } from '../Configuration/textConfiguration';
import { ImpactedAddress } from '../ImpactedAddress';
import { BusinessDetailsDisplay, BusinessSearch } from '../BusinessSearch';
import { ErrorSummaryType } from '../EligibilityPageProjectArchetype';
import DebugEligibilitySummary from '../Debug/DebugEligibilitySummary';
import DebugNote from '../Debug/DebugNote';

// const LabelWrapper = styled.div`
//   display: block;
//   color: #242934;
//   margin-bottom: -1rem;
//   font-weight: 500;
//   line-height: 1.5;
//   margin-top: 1.5rem;
// `;

const RadioWrapper = styled.div`
  margin-top: 2rem;
`;

const yesNoOptions =
    [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' }
    ];

export const BusinessDisasterEligibilityPage = ({ errors, showErrors }: BusinessDisasterEligibilityPageProps) => {
    const { globalState, dispatch } = useApplicationContext();
    const appConfig = useApplicationConfig();
    const eligibility: BusinessDisasterEligibility = globalState.eligibility;
    const business = eligibility.business;
    const disasterName = appConfig.disasterName || '[the disaster]';
    const defaultErrorMessage = 'This field is required.';
    const [addressMode, setAddressMode] = useState<ImpactedAddressMode>('auto');

    const onChangeEvent = (fieldName: string) => value => {
        eligibility[fieldName] = value;
        dispatch({ type: ActionType.UPDATE_ELIGIBILITY_ACTION, payload: { eligibility: eligibility } });
    };

    const onSelectHasAbn = (value: string) => {
        eligibility.hasAbn = value;
        if (eligibility.hasAbn !== 'Yes') {
            eligibility.business = {
                businessName: ''
            };
        }
        dispatch({ type: ActionType.UPDATE_ELIGIBILITY_ACTION, payload: { eligibility: eligibility } });
    };

    const onSetBusinessName = value => {
        set(eligibility, 'business.businessName', value?.target?.value);
        dispatch({ type: ActionType.UPDATE_ELIGIBILITY_ACTION, payload: { eligibility: eligibility } });
    };

    const onSelectBusiness = value => {
        eligibility.business = value;
        dispatch({ type: ActionType.UPDATE_ELIGIBILITY_ACTION, payload: { eligibility: eligibility } });
    };

    const onClearBusiness = () => {
        delete eligibility.business;
        dispatch({ type: ActionType.UPDATE_ELIGIBILITY_ACTION, payload: { eligibility: eligibility } });
    };

    const onSetEmployees = value => {
        set(eligibility, 'employees', value?.target?.value);
        dispatch({ type: ActionType.UPDATE_ELIGIBILITY_ACTION, payload: { eligibility: eligibility } });
    };

    const onSwitchAddressMode = (mode: ImpactedAddressMode) => {
        eligibility['impactedAddressMode'] = mode;
        setAddressMode(mode);
    };

    const isError = (fieldName: string) => typeof errors === 'object' &&
        !!errors[fieldName] &&
        showErrors;

    const errorMessage = (fieldName: string) => (typeof errors === 'object' &&
        (errors[fieldName]?.message ?? defaultErrorMessage)) ||
        '';

    return (<>
        <Row>
            <Col span={6}>
                <RadioWrapper>
                    <FormRadioGroup
                        id='hasAbn'
                        name='hasAbn'
                        legend={getLabel(
                            appConfig,
                            'eligibilityPage',
                            'hasAbn', 'Does your impacted business have a registered ABN?')}
                        vertical={true}
                        options={[
                            { label: 'No, my business is a not-for-profit without an ABN', value: 'No' },
                            { label: 'Yes, my business has an ABN', value: 'Yes' }
                        ]}
                        onChange={onSelectHasAbn}
                        value={eligibility.hasAbn}
                        hasError={isError('hasAbn')}
                        errorMessage={errorMessage('hasAbn')}
                        helpMessage={getHelpMessage(
                            appConfig,
                            'eligibilityPage',
                            'hasAbn', '')}
                    />
                </RadioWrapper>
            </Col>
        </Row>
        {
            eligibility?.hasAbn === 'Yes' &&
            <><Row>
                <Col span={6}>
                    {business?.abn && <BusinessDetailsDisplay business={business} onClearRequest={onClearBusiness} />}
                    {!(business?.abn) &&
                        // eslint-disable-next-line react/jsx-indent
                        <BusinessSearch
                            name='business'
                            // eslint-disable-next-line react/jsx-curly-brace-presence
                            label={'Business ABN, ACN or Name'}
                            onSelect={onSelectBusiness}
                            initialValue={eligibility.business?.abn?.toString() ?? ''}
                            hasError={isError('business.abn')}
                            errorMessage={errorMessage('business.abn')}
                            helpMessage={getHelpMessage(
                                appConfig,
                                'eligibilityPage',
                                'hasAbn', '')}
                        />}
                </Col>
            </Row>
                {business?.abn && business?.abnSuppressed &&
                    <InPageAlert
                        variant='info'
                        title='You’ll need to provide a document'
                    >
                        We’re unable to verify your ABN and business details.
                        You’ll need to provide a document in the next section.
                    </InPageAlert>}
            </>
        }
        {
            eligibility?.hasAbn === 'No' &&
            <><Row>
                <Col span={6}>
                    <FormGroup
                        label='Business Name'
                        hasError={isError('business.businessName')}
                        errorMessage={errorMessage('business.businessName')}
                    >
                        <Input
                            id='businessName'
                            name='businessName'
                            // eslint-disable-next-line react/jsx-curly-brace-presence
                            onChange={onSetBusinessName}
                            value={eligibility.business?.businessName ?? ''}
                            hasError={isError('business.businessName')}
                        />
                    </FormGroup>
                </Col>
            </Row><InPageAlert
                variant='info'
                title='You may need to provide more documents for assessment'
            >
                    Our team will get in touch with you if they need more information about your not-for-profit organisation.
                </InPageAlert></>
        }
        <Row>
            <Col span={6}>
                <RadioWrapper>
                    <FormRadioGroup
                        id='directDamages'
                        name='directDamages'
                        legend={getLabel(
                            appConfig,
                            'eligibilityPage',
                            'directDamages', `Was your business directly damaged by the ${disasterName}?`)}
                        vertical={true}
                        options={yesNoOptions}
                        onChange={onChangeEvent('directDamages')}
                        value={eligibility.directDamages}
                        hasError={isError('directDamages')}
                        errorMessage={errorMessage('directDamages')}
                        helpMessage={getHelpMessage(
                            appConfig,
                            'eligibilityPage',
                            'directDamages', 'Direct damage means damage to your business assets and equipment.')}
                    />
                </RadioWrapper>
            </Col>
        </Row>
        <DebugNote>
            (Storybook only) Regardless of which auto-suggested address is selected below, the mock acts as if MCKELL
            BUILDING was selected (see the Mock Request tab).
        </DebugNote>
        <Row>
            <Col span={6}>
                <RadioWrapper>
                    <ImpactedAddress
                        apiOptions={{
                            key: appConfig.apiKeys.address,
                            outFields: ['lgaName', 'lgaShortName', 'lgaPid']
                        }}
                        manualValidatorConfig={appConfig.addressValidatorConfig}
                        initialMode={addressMode}
                        initialValue={eligibility.impactedAddress ?? {}}
                        label='Impacted Address'
                        onChangeEvent={onChangeEvent('impactedAddress')}
                        onSwitch={onSwitchAddressMode}
                        showErrors={showErrors}
                    />
                    {
                        eligibility.impactedAddress?.length > 0 &&
                        <InPageAlert variant='error' title='Sorry, we can’t progress your application'>
                            Your address is not listed within the disaster affected locations.
                            The {appConfig.productTitle} is available for homes within declared local government areas.
                            Check the eligibility guidelines.
                        </InPageAlert>
                    }
                </RadioWrapper>
                <RadioWrapper>
                    <FormRadioGroup
                        id='impactedAddressSameResidential'
                        name='impactedAddressSameResidential'
                        legend={getLabel(
                            appConfig,
                            'eligibilityPage',
                            'impactedAddressSameResidential',
                            'Is the address of the impacted property the same as your residential address?')}
                        vertical={true}
                        options={yesNoOptions}
                        onChange={onChangeEvent('impactedAddressSameResidential')}
                        value={eligibility.impactedAddressSameResidential}
                        helpMessage={getHelpMessage(
                            appConfig,
                            'eligibilityPage',
                            'impactedAddressSameResidential', '')}
                    />
                </RadioWrapper>
            </Col>
        </Row>
        <Row>
            <Col span={6}>
                <RadioWrapper>
                    <FormRadioGroup
                        id='operatedDuringDisaster'
                        name='operatedDuringDisaster'
                        legend={getLabel(
                            appConfig,
                            'eligibilityPage',
                            'hasAbn',
                            `Was your business/organisation operating during ${disasterName}?`)}
                        vertical={true}
                        options={yesNoOptions}
                        onChange={onChangeEvent('operatedDuringDisaster')}
                        value={eligibility.operatedDuringDisaster}
                        hasError={isError('operatedDuringDisaster')}
                        errorMessage={errorMessage('operatedDuringDisaster')}
                        helpMessage={getHelpMessage(
                            appConfig,
                            'eligibilityPage',
                            'hasAbn', '')}
                    />
                </RadioWrapper>
            </Col>
        </Row>

        <Row>
            <Col span={6}>
                <RadioWrapper>
                    <FormRadioGroup
                        id='intendToReEstablish'
                        name='intendToReEstablish'
                        vertical={true}
                        legend={getLabel(
                            appConfig,
                            'eligibilityPage',
                            'hasAbn',
                            '                              Do you intend to re-establish this business/organisation in the same community or area?')}
                        options={yesNoOptions}
                        onChange={onChangeEvent('intendToReEstablish')}
                        value={eligibility.intendToReEstablish}
                        hasError={isError('intendToReEstablish')}
                        errorMessage={errorMessage('intendToReEstablish')}
                        helpMessage={getHelpMessage(
                            appConfig,
                            'eligibilityPage',
                            'hasAbn', '')}
                    />
                </RadioWrapper>
            </Col>
        </Row>
        <Row>
            <Col span={ 6 }>
                {/* <LabelWrapper>Does your impacted business employ any full-time (or equivalent) staff other than yourself?</LabelWrapper> */}
                <RadioWrapper>
                    <FormRadioGroup
                        id='hasFullTimeEmployees'
                        name='hasFullTimeEmployees'
                        vertical={ true }
                        options={ yesNoOptions }
                        onChange={ onChangeEvent('hasFullTimeEmployees') }
                        value={ eligibility.hasFullTimeEmployees }
                        hasError={ isError('hasFullTimeEmployees') }
                        errorMessage={ errorMessage('hasFullTimeEmployees') }
                        legend='Does your impacted business employ any full-time (or equivalent) staff other than yourself?'
                        helpMessage={ getHelpMessage(
                            appConfig,
                            'eligibilityPage',
                            'hasFullTimeEmployees', 'A full time equivalent is determined by number of hours. For example, if a company defines 40 hours as full time, then two employees working 20 hours per week would count as one full time equivalent.') }
                    />
                </RadioWrapper>
            </Col>
        </Row>

    { eligibility.hasFullTimeEmployees === 'Yes' &&
        <Row>
            <Col span={ 6 }>
                <RadioWrapper>
                    <FormGroup
                        id='employeesGroup'
                        label={ getLabel(
                            appConfig,
                            'eligibilityPage',
                            'employees', 'Number of full-time (or equivalent) employees') }
                        hasError={ isError('employees') }
                        errorMessage={ errorMessage('employees') }
                    >
                        <Input
                            id='employees'
                            name='employees'
                            max={ 999 }
                            min={ 1 }
                            type='number'
                            onChange={ onSetEmployees }
                            value={ eligibility.employees }
                        />
                    </FormGroup>
                </RadioWrapper>
                
            </Col>
        </Row>
    }
        { !!appConfig.maxEmployees && parseInt(eligibility.employees ?? '-1') >= appConfig.maxEmployees &&
            <InPageAlert variant='error' title='We cannot progress your application'>
                Businesses with over {appConfig.maxEmployees} full-time (or equivalent) employees are not eligible for the {appConfig.productTitle} grant.
                <br></br><br></br>The {appConfig.productTitle} grant provides support to impacted small and not-for-profit businesses. Check the eligibility guidelines.
            </InPageAlert> }
            
        { eligibility.hasFullTimeEmployees === 'No' &&
            <Row>
                <Col span={6}>
                    <RadioWrapper>
                        <FormRadioGroup
                            id='soleTrader50PercentIncome'
                            name='soleTrader50PercentIncome'
                            vertical={true}
                            legend={getLabel(
                                appConfig,
                                'eligibilityPage',
                                'hasAbn',
                                'Have you derived more than 50% of your personal income from this business?')}
                            options={yesNoOptions}
                            onChange={onChangeEvent('soleTrader50PercentIncome')}
                            value={eligibility.soleTrader50PercentIncome}
                            hasError={isError('soleTrader50PercentIncome')}
                            errorMessage={errorMessage('soleTrader50PercentIncome')}
                            helpMessage={getHelpMessage(
                                appConfig,
                                'eligibilityPage',
                                'hasAbn', '')}
                        />
                    </RadioWrapper>
                </Col>
            </Row>}

        <Row>
            <Col span={6}>
                <RadioWrapper>
                    <FormRadioGroup
                        id='insurance'
                        name='insurance'
                        vertical={true}
                        legend={getLabel(
                            appConfig,
                            'eligibilityPage',
                            'hasAbn',
                            'Do you have insurance that covers all costs to repair your business?')}
                        options={[
                            { value: 'covered', label: 'Yes, my insurance can cover all costs' },
                            { value: 'not insured', label: 'No, I’m not fully insured' }
                        ]}
                        onChange={onChangeEvent('insurance')}
                        value={eligibility.insurance}
                        hasError={isError('insurance')}
                        errorMessage={errorMessage('insurance')}
                        helpMessage={getHelpMessage(
                            appConfig,
                            'eligibilityPage',
                            'hasAbn', '')}
                    />
                </RadioWrapper>
            </Col>
        </Row>
        {(eligibility.insurance === 'not insured') &&
            <InPageAlert variant='info' title='You may need to provide evidence'>
                If you are partially insured you may need to prove that you cannot claim all costs to repair your business under an insurance policy.
            </InPageAlert>}

        {(eligibility.directDamages === 'No' || eligibility.operatedDuringDisaster === 'No' || eligibility.intendToReEstablish === 'No' || eligibility.insurance === 'covered') &&
            <InPageAlert variant='error' title='We cannot progress your application'>
                Businesses who have not been directly impacted by {disasterName} are not eligible.
                The {appConfig.productTitle} provides support for the costs of clean-up and reinstatement of business operations. Check the eligibility guidelines.
                If you need help, call 13 77 88.
            </InPageAlert>}

        {parseInt(eligibility.employees ?? '-1') === 0 && eligibility.soleTrader50PercentIncome === 'No' &&
            <InPageAlert variant='error' title='Sorry, we can’t progress your application'>
                The {appConfig.productTitle} has been set up to support sole traders that suffer direct damages
                from {disasterName} and derive their primary income from their business
            </InPageAlert>}

        <DebugEligibilitySummary
            pageName='personal disaster eligibility'
            componentName='BusinessDisasterEligibilityPage'
            eligibility={eligibility}
            errors={errors}
            showErrors={showErrors}
        />
    </>
    );
};

interface BusinessDisasterEligibilityPageProps {
    errors: ErrorSummaryType,
    showErrors: boolean
}

BusinessDisasterEligibilityPage.propTypes = {
    errors: PropTypes.object,
    showErrors: PropTypes.bool
};
